import { enums } from "../../enums/enums";
import { AreaServicio } from "../../entities/catalogos/AreaServicio";
import { ICliente } from "../../entities/catalogos/ICliente";
import { IServicioAdicional, ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { destinoGuiaTieneModalidadEntrega, origenGuiaTieneModalidadRecoleccion } from "./guiaRecoleccionOrigenHelpers";

const CARGOS_CITA_EAD = new Set([
    "CARGO POR CITA EAD",
    "FLETE CARGO EAD FUERA DE HORARIO",
    "CARGO POR CITA EAD CEDIS"
]);

const CARGOS_CITA_RAD = new Set([
    "CARGO POR CITA RAD",
    "FLETE CARGO RAD FUERA DE HORARIO",
    "CARGO POR CITA RAD CEDIS"
]);

export const esServicioGuiaSeccionOrigenDestino = (servicio: IServicioAdicional): boolean =>
    servicio?.configuracion?.esDocumentacionGuia === true
    && servicio?.configuracion?.esSolicitudServicioSeccionOrigenDestino === true;

export const configurarServicioOrigenDestinoGuia = (
    servicio: IServicioAdicional,
    esOrigen: boolean
): ServicioAdicional | null => {
    if (!esServicioGuiaSeccionOrigenDestino(servicio)) {
        return null;
    }
    const aplicaOrigen = esOrigen && servicio.configuracion?.esOrigenSolicitudServicio;
    const aplicaDestino = !esOrigen && servicio.configuracion?.esDestinoSolicitudServicio;
    if (!aplicaOrigen && !aplicaDestino) {
        return null;
    }

    const nuevo = new ServicioAdicional(servicio);
    const configuracion = nuevo.configuracion;
    nuevo.esOrigen = esOrigen;
    nuevo.esDestino = !esOrigen;
    nuevo.esRetornoDocumentos = nuevo.id === enums.servicioAdicional.retornoDocumentos;
    nuevo.esAbierto = nuevo.abierto === 1;
    nuevo.categoriaId = configuracion?.conceptoCategoriaSolicitudServicioId;
    nuevo.esSeccionOrigenDestino = true;
    nuevo.esSeccionServiciosAdicionales = false;

    if (nuevo.seleccionado === undefined) {
        nuevo.seleccionado = false;
    }

    nuevo.aplicaOrigen = esOrigen && nuevo.tipoCostoID !== enums.tipoCosto.unicoPorCantidad;
    nuevo.mostrarCantidadOrigen = nuevo.aplicaOrigen && nuevo.tipoCostoID === enums.tipoCosto.cantidad;
    nuevo.aplicaDestino = !esOrigen;
    nuevo.mostrarCantidadDestino = !esOrigen
        && (nuevo.tipoCostoID === enums.tipoCosto.cantidad || nuevo.tipoCostoID === enums.tipoCosto.unicoPorCantidad);

    return nuevo;
};

export const obtenerServiciosAdicionalesOrigenDestinoGuia = (
    servicios: IServicioAdicional[] | undefined,
    esOrigen: boolean,
    cliente?: ICliente,
    areaServicio?: AreaServicio
): ServicioAdicional[] => {
    let lista: ServicioAdicional[] = [];
    (servicios ?? []).forEach(s => {
        const configurado = configurarServicioOrigenDestinoGuia(s, esOrigen);
        if (configurado) {
            lista.push(configurado);
        }
    });

    if (esOrigen && cliente && areaServicio && !(cliente.esRadConCita && areaServicio.esRadConCita)) {
        lista = lista.filter(c => c.nombre !== "CARGO POR CITA RAD");
    }
    if (!esOrigen && cliente && areaServicio && !(cliente.esEadConCita && areaServicio.esEadConCita)) {
        lista = lista.filter(c => c.nombre !== "CARGO POR CITA EAD");
    }

    return lista;
};

const esCargoCitaAutomatico = (servicio: ServicioAdicional, esOrigen: boolean): boolean => {
    const nombre = servicio.nombre ?? servicio.concepto?.nombreConcepto ?? "";
    return esOrigen ? CARGOS_CITA_RAD.has(nombre) : CARGOS_CITA_EAD.has(nombre);
};

const IDS_EXCLUIDOS_ORIGEN_DESTINO = [6, 9, 12, 47] as const;

const esServicioVisibleOrigenDestinoDestinoGuia = (
    servicio: IServicioAdicional,
    paisIdOrigen: number | undefined,
    paisIdDestino: number | undefined,
    servicios: ServicioAdicional[]
): boolean => {
    const ocultarServiciosCruce = paisIdOrigen === paisIdDestino;
    let idsExcluidosOrigenDestino: number[] = [...IDS_EXCLUIDOS_ORIGEN_DESTINO];
    let isShowable = false;

    if (ocultarServiciosCruce) {
        const idsServiciosCruce = servicios
            .filter(e => (e.nombre ?? "").includes("FLETE CRUCE FRONTERIZO"))
            .map(e => e.id);
        idsExcluidosOrigenDestino = [...idsExcluidosOrigenDestino, ...idsServiciosCruce];
        isShowable = idsExcluidosOrigenDestino.includes(servicio.id);
    } else {
        isShowable = !idsExcluidosOrigenDestino.includes(servicio.id);
    }

    return isShowable;
};

export const serviciosDestinoVisiblesGuia = (
    servicios: ServicioAdicional[] | undefined,
    guiaOrigen: IRecoleccion,
    guiaDestino: IRecoleccion
): ServicioAdicional[] => {
    const lista = servicios ?? [];
    const paisIdOrigen = guiaOrigen.colonia?.pais?.id;
    const paisIdDestino = guiaDestino.paisId;

    return lista.filter(s =>
        esServicioVisibleOrigenDestinoDestinoGuia(s, paisIdOrigen, paisIdDestino, lista)
    );
};

export const fusionarServiciosDestinoGuia = (
    catalogo: ServicioAdicional[],
    actuales: ServicioAdicional[] | undefined
): ServicioAdicional[] => {
    const mapaActuales = new Map<number, ServicioAdicional>();
    (actuales ?? []).forEach(s => mapaActuales.set(s.id, s));

    const idsCatalogo = new Set(catalogo.map(s => s.id));
    const resultado = catalogo.map(c => {
        const previo = mapaActuales.get(c.id);
        if (!previo) {
            return c;
        }
        return new ServicioAdicional({
            ...c,
            seleccionado: previo.seleccionado,
            aplicaOrigen: previo.aplicaOrigen,
            aplicaDestino: previo.aplicaDestino,
            cantidad: previo.cantidad,
            cantidadOrigen: previo.cantidadOrigen,
            cantidadDestino: previo.cantidadDestino,
            costo: previo.costo,
            documentos: previo.documentos,
            esRequerido: previo.esRequerido,
        });
    });

    (actuales ?? []).forEach(s => {
        if (!idsCatalogo.has(s.id) && (s.seleccionado || esCargoCitaAutomatico(s, false))) {
            if (s instanceof ServicioAdicional) {
                resultado.push(s);
            } else {
                const props = s as IServicioAdicional;
                const copia = new ServicioAdicional(props);
                copia.convenioId = props.convenioId ?? 0;
                copia.tieneConvenio = !!props.tieneConvenio;
                resultado.push(copia);
            }
        }
    });

    return resultado;
};

export const actualizarServicioDestinoGuia = (
    servicios: ServicioAdicional[] | undefined,
    actualizado: ServicioAdicional
): ServicioAdicional[] => {
    const lista = [...(servicios ?? [])];
    const indice = lista.findIndex(s => s.id === actualizado.id);
    if (indice >= 0) {
        lista[indice] = actualizado;
    } else {
        lista.push(actualizado);
    }
    return lista;
};

export const origenGuiaCompleto = (guiaWizard: IGuiaWizard, guiaOrigen: IRecoleccion): boolean => {
    if (guiaWizard.inHouseStatus !== "ok" || !guiaWizard.sucursalRemitenteId) {
        return false;
    }
    if (guiaOrigen.tipoRecoleccionId !== enums.tipoRecoleccion.comercial) {
        return false;
    }
    return origenGuiaTieneModalidadRecoleccion(guiaOrigen);
};

export const destinoGuiaCompleto = (guiaDestino: IRecoleccion): boolean => {
    if (!guiaDestino.distribuidor?.clienteDistribuidorID) {
        return false;
    }
    if (!guiaDestino.contacto) {
        return false;
    }
    if (!guiaDestino.tipoRecoleccionId) {
        return false;
    }
    return destinoGuiaTieneModalidadEntrega(guiaDestino);
};

export const destinoGuiaMuestraServiciosAdicionales = (guiaDestino: IRecoleccion): boolean => {
    if (!guiaDestino.completado) {
        return false;
    }
    const servicios = guiaDestino.serviciosAdicionales ?? [];
    return servicios.length > 0;
};

export const validarServiciosOrigenDestinoGuia = (
    serviciosOrigen: ServicioAdicional[] | undefined,
    serviciosDestino: ServicioAdicional[] | undefined,
    cliente: ICliente
): string => {
    const totalDocumentosCliente = cliente.documentos != null ? cliente.documentos.length : 0;
    const origenSeleccionados = (serviciosOrigen ?? []).filter(e => e.seleccionado);
    const destinoSeleccionados = (serviciosDestino ?? []).filter(e => e.seleccionado);
    const origenRequeridos = (serviciosOrigen ?? []).filter(e =>
        e.id !== enums.servicioAdicional.retornoDocumentos
        && ((!!e.esRequerido && !e.seleccionado)
            || (e.nombre === "RETORNO DOCUMENTOS" && e.esRetornoDocumentos && !e.seleccionado && totalDocumentosCliente > 0))
        && !destinoSeleccionados.some(s => s.id === e.id));
    const destinoRequeridos = (serviciosDestino ?? []).filter(e =>
        !!e.esRequerido && !e.seleccionado
        && !origenSeleccionados.some(s => s.id === e.id));

    let retornoDocumentos = serviciosOrigen?.find(e => e.esRetornoDocumentos && e.seleccionado);
    retornoDocumentos = retornoDocumentos || serviciosDestino?.find(e => e.esRetornoDocumentos && e.seleccionado);
    let servicioSinDocumentos = retornoDocumentos != null && !retornoDocumentos?.documentos?.length;
    if (retornoDocumentos?.documentos?.length) {
        if (retornoDocumentos.documentos[0]?.documento === "") {
            servicioSinDocumentos = true;
        }
    }

    if (origenRequeridos.length > 0 || destinoRequeridos.length > 0 || servicioSinDocumentos) {
        return origenRequeridos.length > 0 || destinoRequeridos.length > 0
            ? "126"
            : "98";
    }
    return "";
};

export const validarServiciosDestinoCamposGuia = (servicios: ServicioAdicional[] | undefined): string => {
    const servicioSinCantidad = (servicios ?? []).some(e => e.seleccionado && e.CantidadInvalida());
    const servicioSinCosto = (servicios ?? []).some(e => e.seleccionado && e.CostoInvalido());
    if (servicioSinCantidad) {
        return "103";
    }
    if (servicioSinCosto) {
        return "99";
    }
    return "";
};
